import { useQuery } from '@tanstack/react-query';

import { useSession } from '@/compositions/hooks/useSession';
import { platformAccessKeys } from '@/services/platform-access/platform-access.keys';
import { platformAccessService } from '@/services/platform-access/platform-access.service';

interface UsePlatformAdmin {
  isPlatformAdmin: boolean;
  /** `true` enquanto a resposta não chegou — não decida nada nesse intervalo. */
  isLoading: boolean;
}

/**
 * Esta conta é da plataforma.
 *
 * Mora em `compositions/` porque tem dois consumidores de camadas diferentes: a
 * fila de triagem (`modules/platform`) e o menu (`components/layouts/AppMenu`),
 * que não pode importar de `modules/` — o mesmo caminho de `useSession` e
 * `useUnreadNoticesCount`.
 *
 * **Convidado nem pergunta**: sem sessão a RPC responderia `false` de qualquer
 * jeito, e disparar a consulta só gastaria uma ida à rede na primeira tela de
 * quem não tem conta.
 *
 * `staleTime: Infinity` porque quem entra e sai da plataforma é o service role,
 * fora do app: a resposta não muda durante a sessão.
 *
 * ⚠️ **Não é autorização.** Quem barra é a RLS; isto evita tela vazia e item de
 * menu que não leva a lugar nenhum.
 */
export function usePlatformAdmin(): UsePlatformAdmin {
  const { session } = useSession();
  const profileId = session?.userId;

  const { data, isPending } = useQuery({
    queryKey: platformAccessKeys.admin(profileId ?? 'guest'),
    queryFn: () => platformAccessService.amIPlatformAdmin(),
    enabled: Boolean(profileId),
    staleTime: Infinity,
  });

  return {
    isPlatformAdmin: data ?? false,
    // Sem sessão não há espera: a resposta é `false` e é final.
    isLoading: Boolean(profileId) && isPending,
  };
}
